/**
 * 知识抽查系统 - 知识点详情页
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, message, Spin, Empty } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CalendarOutlined } from '@ant-design/icons';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';

const KnowledgeLotteryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [knowledgeContent, setKnowledgeContent] = useState(null);

  useEffect(() => {
    fetchKnowledgeContent();
  }, [id]);

  const fetchKnowledgeContent = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.knowledgeContentApi.getKnowledgeContentById(id);
      if (response.success) {
        setKnowledgeContent(response.data);
      } else {
        message.error(response.message || '获取知识点详情失败');
        navigate('/knowledge-lottery');
      }
    } catch (error) {
      console.error('获取知识点详情失败:', error);
      message.error(error.message || '获取知识点详情失败');
      navigate('/knowledge-lottery');
    }
    setLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  };

  // 判断知识点是否已学习（优先使用后端返回的isLearned字段，否则根据学习日期判断）
  const isLearned = (content) => {
    if (!content) return false;
    // 优先使用后端返回的isLearned字段（基于会议状态）
    if (content.isLearned !== undefined) {
      return content.isLearned;
    }
    // 如果没有isLearned字段，使用原有的逻辑（兼容旧数据）
    if (!content.learnedDate) return false;
    const learnedDate = new Date(content.learnedDate);
    const now = new Date();
    // 只比较日期部分，忽略时间
    learnedDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return learnedDate < now;
  };

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!knowledgeContent) {
    return (
      <div style={{ padding: '48px' }}>
        <Empty description="知识点不存在" />
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Button onClick={() => navigate('/knowledge-lottery')}>
            返回首页
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <header style={{ 
        borderBottom: '4px solid #ff4d4f', 
        background: '#fff',
        padding: '16px 24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/knowledge-lottery')}
            style={{ border: 'none' }}
          >
            返回
          </Button>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
            知识点详情
          </h1>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <Card 
          style={{ 
            border: '2px solid #ff4d4f',
            borderRadius: '8px'
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                {knowledgeContent.title}
              </h2>
              {knowledgeContent.sessionYear && knowledgeContent.sessionNumber && (
                <Tag color="red" style={{ border: '2px solid #ff4d4f' }}>
                  {knowledgeContent.sessionYear}年第{knowledgeContent.sessionNumber}期会议
                </Tag>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CalendarOutlined /> {formatDate(
                knowledgeContent.associatedSession?.sessionDate || 
                knowledgeContent.learnedDate || 
                knowledgeContent.createdAt
              )}
              {isLearned(knowledgeContent) && (
                <Tag color="success" style={{ margin: 0 }}>已学习</Tag>
              )}
            </div>
            {knowledgeContent.categoryId && (
              <div style={{ marginTop: '8px' }}>
                <Tag>分类: {
                  typeof knowledgeContent.categoryId === 'object' 
                    ? knowledgeContent.categoryId.name 
                    : '未分类'
                }</Tag>
              </div>
            )}
            
            {/* 显示所有关联的会议 */}
            {knowledgeContent.associatedSessions && knowledgeContent.associatedSessions.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f0f7ff', borderRadius: '4px', border: '1px solid #d4e8ff' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1890ff' }}>
                  关联会议 ({knowledgeContent.associatedSessions.length} 个)：
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {knowledgeContent.associatedSessions.map((session, index) => {
                    const sessionDate = new Date(session.sessionDate);
                    const now = new Date();
                    sessionDate.setHours(0, 0, 0, 0);
                    now.setHours(0, 0, 0, 0);
                    const isCompleted = now > sessionDate;
                    
                    return (
                      <Tag
                        key={session.id || index}
                        color={isCompleted ? 'success' : 'processing'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/knowledge-lottery/session/${session.id}`)}
                      >
                        {session.title} ({formatDate(session.sessionDate)})
                        {isCompleted ? ' [已完成]' : ' [待进行]'}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <div style={{ 
            whiteSpace: 'pre-wrap', 
            lineHeight: '1.8',
            fontSize: '14px',
            padding: '16px',
            background: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #e8e8e8'
          }}>
            {knowledgeContent.content}
          </div>

          {knowledgeContent.attachmentUrls && knowledgeContent.attachmentUrls.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>附件：</h3>
              {knowledgeContent.attachmentUrls.map((url, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                  </a>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
            <Button 
              type="primary"
              onClick={() => navigate('/knowledge-lottery/manage')}
              style={{ background: '#ff4d4f', borderColor: '#ff4d4f' }}
            >
              前往管理后台
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default KnowledgeLotteryDetail;
